// G41 — force-boot-crash hook for the dev-diagnostics e2e spec. The
// vanilla DOM fallback in index.html is the safety net for "React fails
// to mount." Hard to trigger from outside; this query param lets the e2e
// reliably exercise the fallback path. Tier-gated to non-prod via the
// VITE_DEPLOY_ENV literal so the throw is DCE'd in prod builds.
if (
  import.meta.env.VITE_DEPLOY_ENV !== 'production' &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('__diag_force_boot_crash')
) {
  throw new Error('[dev-diagnostics] force-boot-crash via __diag_force_boot_crash=1');
}

// Phase 00b error-surface S3 (D18): DevDiagnostics capture is FIRST so its
// console patches install before any other code touches console. Tier-gated
// internally; on prod the install body is DCE'd (VITE_DEPLOY_ENV inlined as
// "production" literal). On dev + staging this patches console.error/warn,
// adds window error + unhandledrejection listeners, and drains any pre-mount
// queues into the diagnostics bus for the overlay (S4) to render.
import { install as installDevDiagnostics } from '@/lib/dev-diagnostics/capture';
installDevDiagnostics();

// Phase 00b error-surface S4 (D4): mount the DevDiagnosticsOverlay on its
// OWN React root (#diag-root in index.html) so an App-tree crash leaves the
// overlay rendering. Dev tier mounts unconditionally; staging tier mounts
// only after super_admin sign-in (staging-gate.ts below).
import { mountOverlay } from '@/lib/dev-diagnostics/mount';
mountOverlay();

// Phase 00b error-surface S5 (D20): expose window.iskool.debug.* on non-prod
// tiers. Tier, release, lastErrors (bus snapshot), clear(). NO user info —
// auth UUIDs are stable pivot vectors per the outside-voice review.
import { installIskoolDebug } from '@/lib/iskool-debug';
installIskoolDebug();

// Phase 00b error-surface — G2 of gap completion (D15): on staging tier
// only, subscribe to auth state and mount the overlay when a super_admin
// signs in. Dynamic import keeps the Supabase wiring out of dev + prod
// bundles (chunk only loads when the tier check passes).
if (import.meta.env.VITE_DEPLOY_ENV === 'staging') {
  void import('@/lib/dev-diagnostics/staging-gate').then(({ init }) => init());
}

// GAP-75: Sentry is deferred via boot stub. The stub synchronously installs
// window.onerror + unhandledrejection listeners (queues events) and schedules
// full SDK load via requestIdleCallback. Reduces first-paint critical path
// by ~424KB (Sentry vendor chunk). Queued events are drained once SDK loads.
// Order matters (D18): capture runs FIRST so when Sentry's consoleIntegration
// patches later, it wraps OUR wrapper — both capture and Sentry breadcrumbs
// keep flowing without conflict.
import { deferSentryInit } from '@/lib/sentry-boot';
deferSentryInit();
import React from "react";
import { createRoot } from "react-dom/client";
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import App from "./App.tsx";
import "./index.css";
import { queryClient } from '@/lib/queryClient';
import { persistOptions } from '@/lib/queryPersistence';
import { crossTabSync } from '@/lib/crossTabSync';
import { initGlobalErrorHandlers } from '@/lib/global-error-handler';

// Disable browser's native scroll restoration — the app manages scroll position manually
history.scrollRestoration = 'manual';

// Initialize global error handlers FIRST to catch all errors
// Returns cleanup function for beforeunload
const cleanupErrorHandlers = initGlobalErrorHandlers();

// Initialize cross-tab synchronization
crossTabSync.init(queryClient);

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  crossTabSync.destroy();
  cleanupErrorHandlers();
});

// GAP-77: performance.mark consumer.
// In DEV → console.debug every measure (full observability for local dev).
// In prod → only measures with explicit `prod.*` name prefix forward to Sentry
// as metrics (5% sampled). Other measures (LessonViewer:DisplayDone,
// splitview.resize, square_feed_*, index-load-time, etc.) are debug-only and
// stay out of Sentry — no name-leak noise. fmp.route.* has its own dedicated
// 100%-breadcrumb branch below for real-user paint telemetry.
function trackWebVitals() {
  if ('web-vitals' in window) return;
  performance.mark('app-start');

  // Round 6 — gate dev-only behavior on deploy TIER (VITE_DEPLOY_ENV), not
  // Vite build mode. We force --mode production across all 3 Vercel builds
  // per D13, so .DEV is always false on every deploy; using it here meant
  // dev.iskool.ai got no perf-log verbosity. Direct access for define DCE.
  const IS_DEV_TIER = import.meta.env.VITE_DEPLOY_ENV === 'development';
  const PROD_SAMPLE_RATE = 0.05;

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.entryType !== 'measure') continue;
      if (IS_DEV_TIER) {
        import('@/lib/logger').then(({ logger }) => {
          logger.debug(`Performance: ${entry.name} took ${entry.duration}ms`);
        });
      } else if (entry.name.startsWith('prod.') && Math.random() < PROD_SAMPLE_RATE) {
        import('@/lib/logger').then(({ logger }) => {
          logger.metric(`perf.${entry.name}`, {
            duration_ms: Math.round(entry.duration),
            start_time: Math.round(entry.startTime),
          });
        });
      }
      // Step 44: route-FMP measurements piggyback the observer to land in
      // every Sentry error trail (100%, not sampled). Breadcrumbs are
      // in-memory ring-buffer until an error fires, so the cost is bounded
      // and the value is real-user paint time visible at error time.
      if (!IS_DEV_TIER && entry.name.startsWith('fmp.route.')) {
        const routeKey = entry.name.slice('fmp.route.'.length);
        const durationMs = Math.round(entry.duration);
        import('@/lib/sentry').then(({ Sentry }) => {
          Sentry.addBreadcrumb({
            category: 'perf.fmp',
            message: `route ${routeKey} fmp ${durationMs}ms`,
            level: 'info',
            data: { route: routeKey, duration_ms: durationMs },
          });
        });
      }
    }
  });

  observer.observe({ entryTypes: ['measure'] });
}

trackWebVitals();

// Conditional StrictMode for optimal development performance.
// Round 6 — StrictMode gates on deploy TIER (so dev.iskool.ai gets it),
// not Vite build mode (which is always "production" per D13).
// eslint-disable-next-line react-refresh/only-export-components -- entry-point bootstrap; AppWrapper is module-local and never re-rendered via HMR. Fast-refresh doesn't apply to main.tsx.
const AppWrapper = import.meta.env.VITE_DEPLOY_ENV !== 'production' && import.meta.env.VITE_DISABLE_STRICT_MODE !== 'true'
  ? ({ children }: { children: React.ReactNode }) => <React.StrictMode>{children}</React.StrictMode>
  : ({ children }: { children: React.ReactNode }) => <>{children}</>;

createRoot(document.getElementById("root")!).render(
  <AppWrapper>
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <App />
    </PersistQueryClientProvider>
  </AppWrapper>
);
